type PuppyKernel = ReturnType<typeof import('../../worker/engine/loader').getPuppyKernel>;
type RawPoint3D = [number, number, number];

/** 边曲线代理线采样段数。 */
const EDGE_CURVE_MIN_SAMPLE_SEGMENTS = 24;
/** 圆/椭圆端面轮廓的最小采样段数，避免显示为低分段多边形。 */
const EDGE_CLOSED_CONIC_MIN_SAMPLE_SEGMENTS = 96;
/** 闭合样条端面轮廓的最小采样段数，避免 Puppy CAD 内核 曲线边在显示代理层被误看作折线。 */
const EDGE_CLOSED_SPLINE_MIN_SAMPLE_SEGMENTS = 128;
/** 开放样条边的最小采样段数，保持转换实体引用与边线拾取的视觉连续性。 */
const EDGE_OPEN_SPLINE_MIN_SAMPLE_SEGMENTS = 96;
/** 曲边采样最大段数，避免异常参数域造成过量代理点。 */
const EDGE_CURVE_MAX_SAMPLE_SEGMENTS = 256;
/** 曲边采样角度步长，单位为弧度。 */
const EDGE_CURVE_SAMPLE_ANGLE_STEP = Math.PI / 48;
/** 判断曲线是否可退化为直线时的线性容差。 */
const EDGE_CURVE_LINEAR_TOLERANCE = 1e-5;
/** 判断曲面是否为几何平面时使用的法向与距离容差。 */
const FACE_SURFACE_PLANAR_TOLERANCE = 1e-5;

/**
 * 安全释放 Puppy CAD 内核 对象。
 *
 * @param value 待释放对象。
 */
function safeDelete(value: unknown): void {
  const deleteMethod = (value as { delete?: () => void } | null)?.delete;
  if (typeof deleteMethod !== 'function') {
    return;
  }
  try {
    deleteMethod.call(value);
  } catch {
    // Puppy CAD 内核 清理失败不应覆盖主流程错误。
  }
}

/**
 * 计算三维向量差。
 *
 * @param left 左侧点。
 * @param right 右侧点。
 * @returns 差向量。
 */
function subtractPoint(left: RawPoint3D, right: RawPoint3D): RawPoint3D {
  return [left[0] - right[0], left[1] - right[1], left[2] - right[2]];
}

/**
 * 计算三维向量叉积长度。
 *
 * @param left 左向量。
 * @param right 右向量。
 * @returns 叉积长度。
 */
function crossLength(left: RawPoint3D, right: RawPoint3D): number {
  const x = left[1] * right[2] - left[2] * right[1];
  const y = left[2] * right[0] - left[0] * right[2];
  const z = left[0] * right[1] - left[1] * right[0];
  return Math.hypot(x, y, z);
}

/**
 * 计算三维向量点积。
 *
 * @param left 左向量。
 * @param right 右向量。
 * @returns 点积。
 */
function dotPoint(left: RawPoint3D, right: RawPoint3D): number {
  return left[0] * right[0] + left[1] * right[1] + left[2] * right[2];
}

/**
 * 归一化三维向量。
 *
 * @param vector 待归一化向量。
 * @returns 归一化后的向量；零向量返回 null。
 */
function normalizePoint(vector: RawPoint3D): RawPoint3D | null {
  const length = Math.hypot(vector[0], vector[1], vector[2]);
  if (length <= Number.EPSILON) {
    return null;
  }
  return [vector[0] / length, vector[1] / length, vector[2] / length];
}

/**
 * 解析可用于曲面采样的有限参数。
 *
 * @param first 起始参数。
 * @param last 结束参数。
 * @returns 内部三点采样参数；无有效有限区间时返回 null。
 */
function resolveSurfaceSampleParameters(first: number, last: number): [number, number, number] | null {
  if (!Number.isFinite(first) || !Number.isFinite(last)) {
    return null;
  }
  if (Math.abs(last - first) <= Number.EPSILON) {
    return [first, first, first];
  }
  return [
    first + (last - first) * 0.25,
    first + (last - first) * 0.5,
    first + (last - first) * 0.75,
  ];
}

/**
 * 判断两点是否在曲线采样容差内重合。
 *
 * @param left 左侧点。
 * @param right 右侧点。
 * @returns 是否重合。
 */
function arePointsCoincident(left: RawPoint3D, right: RawPoint3D): boolean {
  return Math.hypot(
    left[0] - right[0],
    left[1] - right[1],
    left[2] - right[2],
  ) <= EDGE_CURVE_LINEAR_TOLERANCE;
}

/**
 * 判断采样点集合是否仍可安全退化为直线段。
 *
 * @param points 采样点集合。
 * @returns `true` 表示所有中间点都落在首尾直线容差内。
 */
function areSampledPointsLinear(points: RawPoint3D[]): boolean {
  if (points.length <= 2) {
    return true;
  }
  const start = points[0]!;
  const end = points[points.length - 1]!;
  if (arePointsCoincident(start, end)) {
    return false;
  }
  const chord = subtractPoint(end, start);
  const chordLength = Math.hypot(chord[0], chord[1], chord[2]);
  if (chordLength <= Number.EPSILON) {
    return true;
  }
  return points.slice(1, -1).every((point) => (
    crossLength(chord, subtractPoint(point, start)) / chordLength <= EDGE_CURVE_LINEAR_TOLERANCE
  ));
}

/**
 * 从曲线适配器中读取可序列化曲线类型。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param curve 曲线适配器。
 * @returns 曲线类型。
 */
function resolveCurveKindFromAdaptor(
  puppy: PuppyKernel,
  curve: { GetType?: () => unknown },
): TopologyCurveKind {
  return typeof curve.GetType === 'function'
    ? mapCurveKindFromPuppyKernel(puppy, curve.GetType())
    : 'other';
}

/**
 * 根据曲线类型与参数跨度计算代理线采样段数。
 *
 * @param curveKind 曲线类型。
 * @param parameterSpan 参数跨度。
 * @returns 采样段数。
 */
function resolveEdgeCurveSampleSegments(
  curveKind: TopologyCurveKind,
  parameterSpan: number,
  isClosedCurve: boolean,
): number {
  const spanSegments = Math.ceil(Math.abs(parameterSpan) / EDGE_CURVE_SAMPLE_ANGLE_STEP);
  const minSegments = resolveEdgeCurveMinimumSampleSegments(curveKind, isClosedCurve);
  return Math.min(
    EDGE_CURVE_MAX_SAMPLE_SEGMENTS,
    Math.max(minSegments, spanSegments),
  );
}

/**
 * 根据曲线类型解析显示代理线的最小采样段数。
 *
 * @param curveKind Puppy CAD 内核 解析出的底层曲线类型。
 * @param isClosedCurve 当前边是否为闭合曲线。
 * @returns 显示代理线的最小采样段数。
 */
function resolveEdgeCurveMinimumSampleSegments(
  curveKind: TopologyCurveKind,
  isClosedCurve: boolean,
): number {
  if (curveKind === 'circle' || curveKind === 'ellipse') {
    return EDGE_CLOSED_CONIC_MIN_SAMPLE_SEGMENTS;
  }
  if (curveKind === 'bezier' || curveKind === 'bspline') {
    return isClosedCurve
      ? EDGE_CLOSED_SPLINE_MIN_SAMPLE_SEGMENTS
      : EDGE_OPEN_SPLINE_MIN_SAMPLE_SEGMENTS;
  }
  return EDGE_CURVE_MIN_SAMPLE_SEGMENTS;
}

/**
 * 从 Puppy CAD 内核 曲线适配器读取闭合语义。
 *
 * @param curve Puppy CAD 内核 曲线适配器。
 * @returns 曲线闭合或周期时返回 true。
 */
function resolveEdgeCurveClosedState(curve: {
  IsClosed?: () => boolean;
  IsPeriodic?: () => boolean;
}): boolean {
  try {
    return Boolean(curve.IsClosed?.() || curve.IsPeriodic?.());
  } catch {
    return false;
  }
}

/**
 * 从 Puppy CAD 内核 边曲线中采样代理线点。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param edgeShape 边形体。
 * @returns 曲线采样点；直线或无法采样时返回 `undefined`。
 */
export function sampleEdgeCurveFromPuppyKernel(
  puppy: PuppyKernel,
  edgeShape: unknown,
): TopologyPoint3D[] | undefined {
  const curveCtor = (puppy as Record<string, unknown>).BRepAdaptor_Curve_2 as
    | (new (edge: unknown) => {
      FirstParameter: () => number;
      LastParameter: () => number;
      Value: (parameter: number) => { X: () => number; Y: () => number; Z: () => number };
      GetType?: () => unknown;
      IsClosed?: () => boolean;
      IsPeriodic?: () => boolean;
      delete?: () => void;
    })
    | undefined;
  if (typeof curveCtor !== 'function') {
    return undefined;
  }

  const curve = new curveCtor(edgeShape);
  try {
    const firstParameter = curve.FirstParameter();
    const lastParameter = curve.LastParameter();
    const parameterSpan = lastParameter - firstParameter;
    if (!Number.isFinite(parameterSpan) || Math.abs(parameterSpan) <= Number.EPSILON) {
      return undefined;
    }

    const curveKind = resolveCurveKindFromAdaptor(puppy, curve);
    const sampleSegments = resolveEdgeCurveSampleSegments(
      curveKind,
      parameterSpan,
      resolveEdgeCurveClosedState(curve),
    );
    const points = Array.from({ length: sampleSegments + 1 }, (_unused, index) => {
      const parameter = firstParameter + (parameterSpan * index) / sampleSegments;
      const point = curve.Value(parameter);
      try {
        return [point.X(), point.Y(), point.Z()] as RawPoint3D;
      } finally {
        safeDelete(point);
      }
    });
    return areSampledPointsLinear(points) ? undefined : points;
  } catch {
    return undefined;
  } finally {
    safeDelete(curve);
  }
}

/**
 * 将 Puppy CAD 内核 曲线枚举映射为可序列化曲线类型。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param curveType Puppy CAD 内核 曲线枚举值。
 * @returns 曲线类型。
 */
function mapCurveKindFromPuppyKernel(puppy: PuppyKernel, curveType: unknown): TopologyCurveKind {
  const curveTypes = (puppy as Record<string, unknown>).GeomAbs_CurveType as Record<string, unknown> | undefined;
  if (!curveTypes) {
    return 'other';
  }
  if (curveType === curveTypes.GeomAbs_Line) {
    return 'line';
  }
  if (curveType === curveTypes.GeomAbs_Circle) {
    return 'circle';
  }
  if (curveType === curveTypes.GeomAbs_Ellipse) {
    return 'ellipse';
  }
  if (curveType === curveTypes.GeomAbs_Hyperbola) {
    return 'hyperbola';
  }
  if (curveType === curveTypes.GeomAbs_Parabola) {
    return 'parabola';
  }
  if (curveType === curveTypes.GeomAbs_BezierCurve) {
    return 'bezier';
  }
  if (curveType === curveTypes.GeomAbs_BSplineCurve) {
    return 'bspline';
  }
  if (curveType === curveTypes.GeomAbs_OffsetCurve) {
    return 'offset';
  }
  return 'other';
}

/**
 * 解析 Puppy CAD 内核 边的底层曲线类型。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param edgeShape 边形体。
 * @returns 曲线类型。
 */
export function resolveEdgeCurveKindFromPuppyKernel(
  puppy: PuppyKernel,
  edgeShape: unknown,
): TopologyCurveKind {
  const curveCtor = (puppy as Record<string, unknown>).BRepAdaptor_Curve_2 as
    | (new (edge: unknown) => {
      GetType?: () => unknown;
      delete?: () => void;
    })
    | undefined;
  if (typeof curveCtor !== 'function') {
    return 'other';
  }
  const curve = new curveCtor(edgeShape);
  try {
    return typeof curve.GetType === 'function'
      ? mapCurveKindFromPuppyKernel(puppy, curve.GetType())
      : 'other';
  } catch {
    return 'other';
  } finally {
    safeDelete(curve);
  }
}

type FaceSurfacePlanarSample = {
  point: RawPoint3D;
  normal: RawPoint3D;
};

/**
 * 在曲面参数点上采样点与法向。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param surface Puppy CAD 内核 曲面适配器。
 * @param u U 参数。
 * @param v V 参数。
 * @returns 采样点与法向；采样失败时返回 null。
 */
function sampleFaceSurfaceForPlanarCheck(
  puppy: PuppyKernel,
  surface: {
    D1: (u: number, v: number, pointArg: unknown, d1u: unknown, d1v: unknown) => void;
  },
  u: number,
  v: number,
): FaceSurfacePlanarSample | null {
  let point: unknown = null;
  let tangentU: unknown = null;
  let tangentV: unknown = null;
  try {
    point = new puppy.gp_Pnt_3(0, 0, 0);
    tangentU = new puppy.gp_Vec_4(0, 0, 0);
    tangentV = new puppy.gp_Vec_4(0, 0, 0);
    surface.D1(u, v, point, tangentU, tangentV);

    const pointValue = point as { X: () => number; Y: () => number; Z: () => number };
    const tangentUValue = tangentU as { X: () => number; Y: () => number; Z: () => number };
    const tangentVValue = tangentV as { X: () => number; Y: () => number; Z: () => number };
    const normal = normalizePoint([
      tangentUValue.Y() * tangentVValue.Z() - tangentUValue.Z() * tangentVValue.Y(),
      tangentUValue.Z() * tangentVValue.X() - tangentUValue.X() * tangentVValue.Z(),
      tangentUValue.X() * tangentVValue.Y() - tangentUValue.Y() * tangentVValue.X(),
    ]);
    if (!normal) {
      return null;
    }

    return {
      point: [pointValue.X(), pointValue.Y(), pointValue.Z()],
      normal,
    };
  } catch {
    return null;
  } finally {
    safeDelete(point);
    safeDelete(tangentU);
    safeDelete(tangentV);
  }
}

/**
 * 判断 Puppy CAD 内核 face 在几何上是否为平面。
 *
 * 设计说明：
 * 1. Puppy CAD 内核 可能把矩形拉伸侧面表示为 SurfaceOfExtrusion，而不是 Plane；
 * 2. 同域简化白名单需要判断几何意义上的平面，否则会漏掉矩形凸台合并；
 * 3. 这里用参数域内部多点法向与同平面距离判定，避免把法向变化的拉伸曲面误判为平面。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param faceShape face 形体。
 * @returns 几何上是否为平面。
 */
export function isFaceSurfaceGeometricallyPlanarFromPuppyKernel(
  puppy: PuppyKernel,
  faceShape: unknown,
): boolean {
  let surface: unknown = null;
  try {
    surface = new puppy.BRepAdaptor_Surface_2(faceShape as never, true);
    const surfaceValue = surface as {
      FirstUParameter: () => number;
      LastUParameter: () => number;
      FirstVParameter: () => number;
      LastVParameter: () => number;
      D1: (u: number, v: number, pointArg: unknown, d1u: unknown, d1v: unknown) => void;
    };
    const uParameters = resolveSurfaceSampleParameters(
      surfaceValue.FirstUParameter(),
      surfaceValue.LastUParameter(),
    );
    const vParameters = resolveSurfaceSampleParameters(
      surfaceValue.FirstVParameter(),
      surfaceValue.LastVParameter(),
    );
    if (!uParameters || !vParameters) {
      return false;
    }

    const samples = uParameters.flatMap((uParameter) => (
      vParameters.map((vParameter) => sampleFaceSurfaceForPlanarCheck(
        puppy,
        surfaceValue,
        uParameter,
        vParameter,
      ))
    ));
    if (samples.some((sample) => !sample)) {
      return false;
    }

    const resolvedSamples = samples as FaceSurfacePlanarSample[];
    const baseSample = resolvedSamples[0]!;
    return resolvedSamples.every((sample) => {
      const normalDot = Math.abs(dotPoint(baseSample.normal, sample.normal));
      if (Math.abs(1 - normalDot) > FACE_SURFACE_PLANAR_TOLERANCE) {
        return false;
      }
      const pointDelta = subtractPoint(sample.point, baseSample.point);
      return Math.abs(dotPoint(pointDelta, baseSample.normal)) <= FACE_SURFACE_PLANAR_TOLERANCE;
    });
  } catch {
    return false;
  } finally {
    safeDelete(surface);
  }
}

/**
 * 将 Puppy CAD 内核 曲面枚举映射为可序列化曲面类型。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param surfaceType Puppy CAD 内核 曲面枚举值。
 * @returns 曲面类型。
 */
function mapSurfaceKindFromPuppyKernel(puppy: PuppyKernel, surfaceType: unknown): TopologySurfaceKind {
  const surfaceTypes = (puppy as Record<string, unknown>).GeomAbs_SurfaceType as Record<string, unknown> | undefined;
  if (!surfaceTypes) {
    return 'other';
  }
  if (surfaceType === surfaceTypes.GeomAbs_Plane) {
    return 'plane';
  }
  if (surfaceType === surfaceTypes.GeomAbs_Cylinder) {
    return 'cylinder';
  }
  if (surfaceType === surfaceTypes.GeomAbs_Cone) {
    return 'cone';
  }
  if (surfaceType === surfaceTypes.GeomAbs_Sphere) {
    return 'sphere';
  }
  if (surfaceType === surfaceTypes.GeomAbs_Torus) {
    return 'torus';
  }
  if (surfaceType === surfaceTypes.GeomAbs_BezierSurface) {
    return 'bezier';
  }
  if (surfaceType === surfaceTypes.GeomAbs_BSplineSurface) {
    return 'bspline';
  }
  if (surfaceType === surfaceTypes.GeomAbs_SurfaceOfRevolution) {
    return 'surface-of-revolution';
  }
  if (surfaceType === surfaceTypes.GeomAbs_SurfaceOfExtrusion) {
    return 'surface-of-extrusion';
  }
  if (surfaceType === surfaceTypes.GeomAbs_OffsetSurface) {
    return 'offset';
  }
  return 'other';
}

/**
 * 解析 Puppy CAD 内核 面的底层曲面类型。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param faceShape 面形体。
 * @returns 曲面类型。
 */
export function resolveFaceSurfaceKindFromPuppyKernel(
  puppy: PuppyKernel,
  faceShape: unknown,
): TopologySurfaceKind {
  let surface: unknown = null;
  try {
    surface = new puppy.BRepAdaptor_Surface_2(faceShape as never, true);
    const surfaceValue = surface as { GetType?: () => unknown };
    return typeof surfaceValue.GetType === 'function'
      ? mapSurfaceKindFromPuppyKernel(puppy, surfaceValue.GetType())
      : 'other';
  } catch {
    return 'other';
  } finally {
    safeDelete(surface);
  }
}
